import { useState } from "react";

import { TypeToConfirmDialog } from "@/components/common/TypeToConfirmDialog";
import type { GeneratePreviewsRequest } from "@/types/previews";

import { GeneratePreviewDialog } from "./GeneratePreviewDialog";

// CR-112: both batch buttons are now gated by a type-to-confirm phrase.
// `run-batch` is the chosen friction phrase — short enough to muscle-
// memory-break a reflexive click, long enough to rule out a stray Enter
// in the search bar.
const BATCH_CONFIRM_PHRASE = "run-batch" as const;

export interface BatchActionsBarProps {
  readonly disabled: boolean;
  onTrigger(req: GeneratePreviewsRequest): void;
}

// CR-110: "Generate preview" sits left of "Regenerate stale" (most common →
// most ceremonial, per synthesis). Opens the picker dialog; on submit, we
// reuse the same onTrigger path as the per-row Regenerate so polling /
// progress panel behaviour stays identical.
export function BatchActionsBar({
  disabled,
  onTrigger,
}: BatchActionsBarProps): JSX.Element {
  const [forceAllConfirmOpen, setForceAllConfirmOpen] = useState(false);
  const [regenStaleConfirmOpen, setRegenStaleConfirmOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="theme-previews-page__actions">
      <button
        type="button"
        className="btn btn--primary"
        disabled={disabled}
        onClick={() => setPickerOpen(true)}
      >
        Generate preview
      </button>
      <button
        type="button"
        className="btn btn--primary"
        disabled={disabled}
        onClick={() => setRegenStaleConfirmOpen(true)}
      >
        Regenerate stale
      </button>
      <button
        type="button"
        className="btn btn--danger"
        disabled={disabled}
        onClick={() => setForceAllConfirmOpen(true)}
      >
        Force regenerate all
      </button>
      <TypeToConfirmDialog
        open={regenStaleConfirmOpen}
        title="Regenerate stale previews"
        message="This regenerates preview images for every theme whose stored hash no longer matches the current theme config. Cost scales with the number of stale themes; pod throughput is ~5 images / minute."
        phrase={BATCH_CONFIRM_PHRASE}
        confirmLabel="Regenerate stale"
        confirmVariant="primary"
        onConfirm={() => {
          setRegenStaleConfirmOpen(false);
          onTrigger({ force: false });
        }}
        onCancel={() => setRegenStaleConfirmOpen(false)}
      />
      <TypeToConfirmDialog
        open={forceAllConfirmOpen}
        title="Regenerate every theme preview"
        message="This regenerates every theme preview from scratch regardless of staleness. Expect ~10 minutes for the full catalog."
        phrase={BATCH_CONFIRM_PHRASE}
        confirmLabel="Regenerate"
        confirmVariant="danger"
        onConfirm={() => {
          setForceAllConfirmOpen(false);
          onTrigger({ force: true });
        }}
        onCancel={() => setForceAllConfirmOpen(false)}
      />
      <GeneratePreviewDialog
        open={pickerOpen}
        onGenerate={(req) => {
          setPickerOpen(false);
          onTrigger(req);
        }}
        onCancel={() => setPickerOpen(false)}
      />
    </div>
  );
}
